/**
 * Minimum margin (margin floor · 最低毛利率) — pure, zero I/O.
 *
 * The SO guide ("SO: Business & Operations Guide", Notion 3b789adb) says an
 * edit or new line whose profit margin drops below company standards puts the
 * order into a Low Margin Hold (`PENDING_APPROVAL`, released by a manager).
 * PRD §engines / §M7-ap say a below-floor quote shows a warning and needs
 * manager approval. NEITHER names the number, and neither says whether the
 * standard is a MARGIN (on the sell price) or a MARKUP (on cost). This module
 * fixes the vocabulary so the two can never be confused in code:
 *
 *   markup% = (price − cost) / cost  × 100      加價率 — on COST
 *   margin% = (price − cost) / price × 100      毛利率 — on SELL PRICE
 *
 *   cost 100 · price 160  ⇒  markup 60%  ·  margin 37.5%
 *   margin m  ⇔  markup m / (1 − m)     ·     markup k  ⇔  margin k / (1 + k)
 *
 * The FLOOR is a MARGIN (on price), compared against the engine's own
 * `marginVsLanded` (resolvePrice.ts: unitPrice − landedCost, pct on price) —
 * so the number the operator reads on the line is the number the floor is
 * tested against. NetSuite's "Est. Gross Profit Percent" is the same
 * definition (GP ÷ amount; live account 4876698 SO0817254: 157.65 ÷ 278.00 =
 * 56.7086%), so the two systems' percentages are comparable 1:1.
 *
 * The floor VALUE is not decided here (the decision owner rules it; absent
 * until then). Everything is parametrised on `floorPct`; an ABSENT floor
 * evaluates to `no_floor` and moves nothing.
 *
 * Boundary: AT the floor is OK; STRICTLY BELOW is the hold. Both sides are
 * compared at ROUND_HALF_UP 2dp — the displayed margin and the tested margin
 * are the same rounded figure, so a line whose margin reads exactly the floor
 * can never be held.
 *
 * Unknown ≠ below: a line whose landed cost is unknown (no cost record, or a
 * locked-currency row the build-up cannot be compared with) evaluates to
 * `unknown` and NEVER to `below_floor` — the confirm checklist's
 * `price_resolvable` item owns that case, and a hold raised on a number
 * nobody has is a hold nobody can release with a reason.
 */

import { roundHalfUp2 } from './quantityPricing';

export type MarginFloorStatus = 'no_floor' | 'unknown' | 'ok' | 'below_floor';

export interface MarginFloorInput {
  /** Transacted (or resolved) unit price; null/≤0 ⇒ unknown. */
  unitPrice: number | null;
  /** Landed cost the margin is measured against; null ⇒ unknown. */
  landedCost: number | null;
  /** Company minimum margin, percent of sell price. null/undefined ⇒ no floor. */
  floorPct: number | null | undefined;
  /**
   * false when the price and the landed cost are NOT in the same currency —
   * the resolver's own signal is `marginVsLanded === null` while a landed
   * cost is known (a locked-currency row against a base-currency build-up).
   * A cross-currency margin is never compared: it evaluates to `unknown`,
   * never to `below_floor`. Defaults to true.
   */
  comparable?: boolean;
}

export interface MarginFloorEvaluation {
  status: MarginFloorStatus;
  /** (price − cost) / price × 100, 2dp; null when unknown. */
  marginPct: number | null;
  /** (price − cost) / cost × 100, 2dp; null when unknown or cost ≤ 0. */
  markupPct: number | null;
  /** The floor that was tested (2dp); null when no floor. */
  floorPct: number | null;
  /** floor − margin when below (2dp, > 0); null otherwise. */
  shortfallPct: number | null;
}

/** 毛利率 — margin on the SELL PRICE. Null when the price is unknown or ≤ 0. */
export function marginPctOf(unitPrice: number | null, cost: number | null): number | null {
  if (unitPrice === null || cost === null || !(unitPrice > 0)) return null;
  return roundHalfUp2(((unitPrice - cost) / unitPrice) * 100);
}

/** 加價率 — markup on COST. Null when either side is unknown or cost ≤ 0. */
export function markupPctOf(unitPrice: number | null, cost: number | null): number | null {
  if (unitPrice === null || cost === null || !(cost > 0)) return null;
  return roundHalfUp2(((unitPrice - cost) / cost) * 100);
}

/** margin m% ⇒ the markup that yields it: m / (1 − m). Null at/above 100%. */
export function marginToMarkupPct(marginPct: number): number | null {
  if (marginPct >= 100) return null;
  return roundHalfUp2((marginPct / (100 - marginPct)) * 100);
}

/** markup k% ⇒ the margin it yields: k / (1 + k). Null below −100%. */
export function markupToMarginPct(markupPct: number): number | null {
  if (markupPct <= -100) return null;
  return roundHalfUp2((markupPct / (100 + markupPct)) * 100);
}

/**
 * Evaluate one line against the company floor.
 *
 * `floorPct` is normalised to 2dp so the comparison is between two displayed
 * figures. A floor of 0 is a real floor (negative margin holds); only
 * null/undefined means "no floor configured".
 */
export function evaluateMarginFloor(input: MarginFloorInput): MarginFloorEvaluation {
  const floorPct =
    input.floorPct === null || input.floorPct === undefined ? null : roundHalfUp2(input.floorPct);
  const comparable = input.comparable !== false;
  const marginPct = comparable ? marginPctOf(input.unitPrice, input.landedCost) : null;
  const markupPct = comparable ? markupPctOf(input.unitPrice, input.landedCost) : null;

  if (floorPct === null) {
    return { status: 'no_floor', marginPct, markupPct, floorPct: null, shortfallPct: null };
  }
  if (marginPct === null) {
    return { status: 'unknown', marginPct: null, markupPct, floorPct, shortfallPct: null };
  }
  if (marginPct < floorPct) {
    return {
      status: 'below_floor',
      marginPct,
      markupPct,
      floorPct,
      shortfallPct: roundHalfUp2(floorPct - marginPct),
    };
  }
  return { status: 'ok', marginPct, markupPct, floorPct, shortfallPct: null };
}

/** Convenience: true only for a KNOWN, COMPARABLE margin strictly below a SET floor. */
export function isBelowMarginFloor(
  unitPrice: number | null,
  landedCost: number | null,
  floorPct: number | null | undefined,
  comparable: boolean = true
): boolean {
  return (
    evaluateMarginFloor({ unitPrice, landedCost, floorPct, comparable }).status === 'below_floor'
  );
}

/**
 * Whether a priced line's margin may be compared at all: a landed cost is
 * known, the resolver did not refuse the comparison (its persisted
 * `marginVsLanded` is null when a foreign locked row beat a base-currency
 * build-up, or when nothing resolved), AND the price currency equals the cost
 * currency when both are known (base mode never refuses on its own).
 * Structural on purpose — the SO line, the draft line and the trace panel all
 * satisfy it.
 */
export function lineMarginComparable(line: {
  landedCost: number | null;
  /** The document / line currency the transacted price is in (null = unknown). */
  currency?: string | null;
  priceTrace: { marginVsLanded: unknown; currency?: string | null } | null;
}): boolean {
  if (line.landedCost === null) return false;
  if (line.priceTrace === null) return true;
  // The resolver refused the comparison itself (currency mode, foreign locked row).
  if (line.priceTrace.marginVsLanded === null) return false;
  // Base mode never refuses — the order path resolves without `currency`, so a
  // USD order line would otherwise be compared against an AUD landed cost. The
  // snapshot's `currency` is the cost build-up's currency in base mode; when
  // both sides are known and differ, the margin is not comparable.
  const priceCurrency = line.currency ?? null;
  const costCurrency = line.priceTrace.currency ?? null;
  if (priceCurrency && costCurrency && priceCurrency !== costCurrency) return false;
  return true;
}
